/**
 * ServicePicker — reusable search + quick-create widget for picking a service.
 *
 * - Searches existing services (excluding those in `excludeIds`).
 * - If the typed text has no exact match, offers to CREATE a new service
 *   inline (name + type) without leaving the current flow.
 *
 * onPick(service, isNew) is called with the full service object once chosen
 * (either an existing one or a freshly-created one).
 */
import React, { useEffect, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

export default function ServicePicker({ companyId, excludeIds = [], onPick, autoFocus = true }) {
  const [services, setServices] = useState([]);
  const [types, setTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [newType, setNewType] = useState('other');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      api.getServices(companyId).catch(() => []),
      api.getServicesMeta(companyId).catch(() => ({})),
    ]).then(([svcs, m]) => {
      setServices(Array.isArray(svcs) ? svcs : (svcs.services || []));
      if (m.service_types) setTypes(m.service_types);
    });
  }, [companyId]);

  const excludeSet = new Set(excludeIds);
  const q = search.trim().toLowerCase();
  const filtered = services.filter(s => {
    if (excludeSet.has(s.id)) return false;
    if (!q) return true;
    return (s.display_name || s.name || '').toLowerCase().includes(q)
      || (s.vendor || '').toLowerCase().includes(q);
  });
  const exactMatch = services.some(s =>
    (s.name || '').toLowerCase() === q || (s.display_name || '').toLowerCase() === q
  );
  const canCreate = q.length > 1 && !exactMatch;

  const handleCreate = async () => {
    if (!search.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const svc = await api.createService({ name: search.trim(), service_type: newType }, companyId);
      // Keep local list in sync so it can be re-picked / excluded properly
      setServices(prev => [...prev, svc]);
      onPick?.(svc, true);
      setSearch('');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handlePickExisting = (svc) => {
    onPick?.(svc, false);
    setSearch('');
  };

  return (
    <div>
      <input
        className="form-control form-control-sm"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Buscar servicio… o escribe un nombre nuevo para crearlo"
        autoFocus={autoFocus}
        onKeyDown={e => {
          if (e.key === 'Enter' && canCreate && !busy) { e.preventDefault(); handleCreate(); }
        }}
      />

      {error && (
        <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4 }}>⚠ {error}</div>
      )}

      {/* Results / create */}
      {(q || filtered.length > 0) && (
        <div style={{
          border: '1px solid var(--border)', borderRadius: 6,
          maxHeight: 220, overflowY: 'auto', marginTop: 6,
            background: 'var(--bg-surface)',
        }}>
          {filtered.map(svc => (
            <div key={svc.id}
              onClick={() => handlePickExisting(svc)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 12px', cursor: 'pointer', fontSize: '0.82rem',
                borderBottom: '1px solid var(--border)',
              }}
              onMouseOver={e => e.currentTarget.style.background = 'var(--bg-toolbar)'}
              onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{svc.display_name || svc.name}</span>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                {svc.service_type_label || svc.service_type}
              </span>
            </div>
          ))}

          {/* Quick-create row */}
          {canCreate && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px', background: 'var(--bg-toolbar)',
              borderTop: filtered.length > 0 ? '1px solid var(--border)' : 'none',
            }}>
              <button type="button" onClick={handleCreate} disabled={busy}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  background: 'var(--accent)', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '5px 10px', fontSize: '0.78rem', fontWeight: 600,
                  cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap',
                }}>
                <PlusIcon style={{ width: 13, height: 13 }} />
                {busy ? 'Creando…' : <>Crear «{search.trim()}»</>}
              </button>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>como</span>
              <select value={newType} onChange={e => setNewType(e.target.value)}
                style={{
                  fontSize: '0.75rem', padding: '3px 6px', borderRadius: 5,
                  border: '1px solid var(--border)', background: 'var(--bg-input)',
                  color: 'var(--text-primary)', flex: 1, minWidth: 0,
                }}>
                {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          )}

          {q && filtered.length === 0 && !canCreate && (
            <div style={{ padding: '8px 12px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Sin resultados.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
